<template>
    <validation-observer v-slot="{ validate }" slim>
        <form @submit.prevent="store(validate)" v-if="Object.keys(booking).length" class="mb-4 p-2">
            <div class="row">
                <div class="col card p-3 mx-2">
                    <div class="d-flex justify-content-between">
                        <h4>{{ $t('Rentee') }}</h4>
                        <app-custom-checkbox id="existing_client" v-model="existing_client" name="existing_client" @input="existingClientClicked">
                            {{ $t('Existing client') }}
                        </app-custom-checkbox>
                    </div>
                    <div class="row">
                        <div class="col-md-6 col-sm-12">
                            <app-select
                                v-if="existing_client"
                                id="clients"
                                v-model="chosen_client"
                                :options="clients"
                                searchable
                                not-empty
                                closeOnSelect
                                label="name"
                                @input="chooseClient">
                                <template #label>
                                    {{ $t('Choose') }}*
                                </template>
                            </app-select>
                            <app-input v-else id="rentee_name" v-model="form.contract.rentee.name" name="rentee_name" rules="required">
                                <template #label>
                                    {{ $t('Name') }}*
                                </template>
                            </app-input>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <app-input id="rentee_idcode" v-model="form.contract.rentee.idcode" name="rentee_idcode" rules="required">
                                <template #label>
                                    {{ $t('Id code') }}*
                                </template>
                            </app-input>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <app-input id="rentee_drivers_license"
                                       v-model="form.contract.rentee.drivers_license"
                                       name="rentee_drivers_license"
                                       rules="required">
                                <template #label>
                                    {{ $t('Drivers license no') }}*
                                </template>
                            </app-input>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <app-input id="rentee_passport" v-model="form.contract.rentee.passport" name="rentee_passport">
                                <template #label>
                                    {{ $t('Passport no') }}
                                </template>
                            </app-input>
                        </div>
                        <div class="col-md-12 col-sm-12">
                            <app-input id="rentee_address" v-model="form.contract.rentee.address" name="rentee_address" rules="required">
                                <template #label>
                                    {{ $t('Address') }}*
                                </template>
                            </app-input>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <app-input id="rentee_phone" v-model="form.contract.rentee.phone" name="rentee_phone" @blur="checkClientDiscount" rules="required">
                                <template #label>
                                    {{ $t('Phone') }}*
                                </template>
                            </app-input>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <app-input id="rentee_email" v-model="form.contract.rentee.email" name="rentee_email" @blur="checkClientDiscount" rules="required">
                                <template #label>
                                    {{ $t('Email') }}*
                                </template>
                            </app-input>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <app-input id="rentee_cc_no" v-model="form.contract.rentee.cc_no" name="rentee_cc_no">
                                <template #label>
                                    {{ $t('Creditcard no') }}
                                </template>
                            </app-input>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <app-input id="rentee_cc_validity" v-model="form.contract.rentee.cc_validity" name="rentee_cc_validity">
                                <template #label>
                                    {{ $t('Creditcard expiry') }}
                                </template>
                            </app-input>
                        </div>
                    </div>
                    <h4 class="mt-3 mb-0">{{ $t('Company') }}</h4>
                    <div class="row">
                        <div class="col-md-4 col-sm-12">
                            <app-input id="company_name" v-model="form.contract.company.name" name="company_name">
                                <template #label>
                                    {{ $t('Name') }}
                                </template>
                            </app-input>
                        </div>
                        <div class="col-md-4 col-sm-12">
                            <app-input id="company_reg_no" v-model="form.contract.company.reg_no" name="company_reg_no">
                                <template #label>
                                    {{ $t('Reg no') }}
                                </template>
                            </app-input>
                        </div>
                        <div class="col-md-4 col-sm-12">
                            <app-input id="company_email" v-model="form.contract.company.email" name="company_email">
                                <template #label>
                                    {{ $t('Invoice email') }}
                                </template>
                            </app-input>
                        </div>
                    </div>
                    <div class="my-2 d-flex justify-content-start">
                        <app-custom-checkbox id="add_client" v-model="form.add_client" name="add_client">
                            {{ $t(existing_client ? 'Update client' : 'Save with client') }}
                        </app-custom-checkbox>
                    </div>
                    <div class="row">
                        <div class="col-md-6 col-sm-12">
                            <app-textarea id="eq_and_serv" v-model="form.contract.info.eq_and_serv" name="eq_and_serv" :rows="3">
                                <template #label>
                                    {{ $t('Equipment/services') }}
                                </template>
                            </app-textarea>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <app-textarea id="extra_info" v-model="form.contract.info.extra_info" name="extra_info" :rows="3">
                                <template #label>
                                    {{ $t('Extra info') }}
                                </template>
                            </app-textarea>
                        </div>
                    </div>
                    <hr>
                    <div class="row">
                        <div class="col-md-12 col-sm-12">
                            <div class="d-flex justify-content-between">
                                <h4>{{ $t('Rental prices') }}</h4>
                                <div>
                                    <app-radio
                                        id="edit_price_no"
                                        v-model="booking.edit_price"
                                        name="edit_price_no"
                                        :label="$t('Automatically calculated price')"
                                        :val="false"
                                        inline/>
                                    <app-radio
                                        id="edit_price_yes"
                                        v-model="booking.edit_price"
                                        name="edit_price_yes"
                                        :label="$t('Manually added price')"
                                        :val="true"
                                        inline/>
                                </div>
                            </div>
                            <template v-if="!booking.edit_price">
                                <div class="row">
                                    <div class="col">
                                        <label class="mb-0">{{ $t('Days') }}</label>
                                        <p class="custom-gray-input-bg">{{ booking.prices.days }}</p>
                                    </div>
                                    <div class="col">
                                        <label class="mb-0">{{ $t('Day price') }}</label>
                                        <p class="custom-gray-input-bg">{{ booking.prices.avg_price }} €</p>
                                    </div>
                                    <div class="col">
                                        <label class="mb-0">{{ $t('Eqipment price') }}</label>
                                        <p class="custom-gray-input-bg">{{ booking.prices.eq_total }} €</p>
                                    </div>
                                    <div class="col">
                                        <label class="mb-0">{{ $t('Services') }}</label>
                                        <p class="custom-gray-input-bg">{{ booking.prices.service_total }} €</p>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col">
                                        <label class="mb-0">{{ $t('Extra hours') }}</label>
                                        <p class="custom-gray-input-bg">{{ booking.prices.over_hrs }}</p>
                                    </div>
                                    <div class="col">
                                        <label class="mb-0">{{ $t('Extra hour price') }}</label>
                                        <p class="custom-gray-input-bg">{{ booking.prices.hr_price }} €</p>
                                    </div>
                                    <div class="col">
                                        <label class="mb-0">{{ $t('Bond') }}</label>
                                        <p class="custom-gray-input-bg">{{ booking.prices.bond }} €</p>
                                    </div>
                                    <div class="col">
                                        <label class="mb-0">{{ $t('Total price') }}</label>
                                        <p class="custom-gray-input-bg">{{ booking.prices.total }} €</p>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="row">
                                    <div class="col">
                                        <app-input id="days" v-model="booking.prices.days" name="days" rules="required" type="number" :step="'0.01'">
                                            <template #label>{{ $t('Days') }} *</template>
                                        </app-input>
                                    </div>
                                    <div class="col">
                                        <app-input id="avg_price" v-model="booking.prices.avg_price" name="avg_price" rules="required" type="number" :step="'0.01'">
                                            <template #label>{{ $t('Day price') }} *</template>
                                        </app-input>
                                    </div>
                                    <div class="col">
                                        <app-input id="eq_total" v-model="booking.prices.eq_total" name="eq_total" rules="required" type="number" :step="'0.01'">
                                            <template #label>{{ $t('Eqipment price') }} *</template>
                                        </app-input>
                                    </div>
                                    <div class="col">
                                        <app-input id="service_total"
                                                   v-model="booking.prices.service_total"
                                                   name="service_total"
                                                   rules="required"
                                                   type="number"
                                                   :step="'0.01'">
                                            <template #label>{{ $t('Services') }} *</template>
                                        </app-input>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col">
                                        <app-input id="over_hrs" v-model="booking.prices.over_hrs" name="over_hrs" type="number">
                                            <template #label>{{ $t('Extra hours') }}</template>
                                        </app-input>
                                    </div>
                                    <div class="col">
                                        <app-input id="hr_price" v-model="booking.prices.hr_price" name="hr_price" type="number" :step="'0.01'">
                                            <template #label>{{ $t('Extra hour price') }}</template>
                                        </app-input>
                                    </div>
                                    <div class="col">
                                        <app-input id="bond" v-model="booking.prices.bond" name="bond" rules="required" type="number" :step="'0.01'">
                                            <template #label>{{ $t('Bond') }} *</template>
                                        </app-input>
                                    </div>
                                    <div class="col">
                                        <app-input id="total" v-model="booking.prices.total" name="total" rules="required" type="number" :step="'0.01'">
                                            <template #label>{{ $t('Total price') }} *</template>
                                        </app-input>
                                    </div>
                                </div>
                            </template>
                            <p v-if="booking.edit_price" class="text-danger text-center">
                                {{ $t('Discounts and promos will NOT be subtracted') }}
                            </p>
                            <div v-if="!booking.edit_price" class="row mb-1">
                                <div class="col">
                                    <template v-if="booking.promo_id">
                                        <div class="form-group mb-1">
                                            <label class="mb-0">{{ $t('Promocode') }}</label>
                                            <p class="custom-gray-input-bg">-{{ booking.promo.discount_percent }}%</p>
                                        </div>
                                    </template>
                                    <template v-else-if="booking.client_id !== null">
                                        <div class="form-group mb-1">
                                            <label class="mb-0">{{ $t('Client') }}</label>
                                            <p class="custom-gray-input-bg">-{{ booking.client.discount_percent }}%</p>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="form-group">
                        <app-button :loading="loading" class="m-0 mt-3" small>
                            {{ $t('Save') }}
                        </app-button>
                    </div>
                </div>
                <div class="col card p-3 mx-2">
                    <div class="d-flex justify-content-between">
                        <h4>{{ $t('Vehicle') }}</h4>
                        <app-custom-checkbox id="manual_vehicle" v-model="manual_vehicle" name="manual_vehicle" @input="manualVehicleClicked">
                            {{ $t('Add vehicle manually') }}
                        </app-custom-checkbox>
                    </div>
                    <template v-if="manual_vehicle">
                        <div class="row">
                            <div class="col-md-4 col-sm-12">
                                <app-input id="make_name" v-model="booking.vehicle.make.name" name="make_name">
                                    <template #label>
                                        {{ $t('Make') }}
                                    </template>
                                </app-input>
                            </div>
                            <div class="col-md-4 col-sm-12">
                                <app-input id="model_name" v-model="booking.vehicle.model.name" name="model_name">
                                    <template #label>
                                        {{ $t('Model') }}
                                    </template>
                                </app-input>
                            </div>
                            <div class="col-md-4 col-sm-12">
                                <app-input id="reg_no" v-model="booking.vehicle.reg_no" name="reg_no">
                                    <template #label>
                                        {{ $t('Reg no') }}
                                    </template>
                                </app-input>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6 col-sm-12">
                                <app-input id="fuel_name" v-model="booking.vehicle.fuel.name" name="fuel_name">
                                    <template #label>
                                        {{ $t('Fuel') }}
                                    </template>
                                </app-input>
                            </div>
                            <div class="col-md-6 col-sm-12">
                                <app-input id="vin" v-model="booking.vehicle.vin" name="vin">
                                    <template #label>
                                        {{ $t('VIN') }}
                                    </template>
                                </app-input>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <app-select
                            id="vehicle_id"
                            v-model="booking.vehicle_id"
                            :options="vehicles.map(c => c.id)"
                            rules="required"
                            name="vehicle_id"
                            openDirection="bottom"
                            searchable
                            not-empty
                            closeOnSelect
                            @input="id => getPrice(id)"
                            :custom-label="id => getVehicleName(id)">
                            <template #label>
                                {{ $t('Vehicle') }}*
                            </template>
                        </app-select>
                    </template>
                    <div class="row mt-3">
                        <div class="col-md-3 col-sm-12 d-flex align-items-center">
                            <app-custom-checkbox id="additional_drivers" v-model="form.contract.info.additional_drivers" name="additional_drivers">
                                {{ $t('Additional drivers permitted') }}
                            </app-custom-checkbox>
                        </div>
                        <div class="col-md-3 col-sm-12 d-flex align-items-center">
                            <app-custom-checkbox id="vehicle_clean" v-model="form.contract.info.vehicle_clean" name="vehicle_clean">
                                {{ $t('Vehicle clean') }}
                            </app-custom-checkbox>
                        </div>
                        <div class="col-md-3 col-sm-12 d-flex align-items-center">
                            <app-custom-checkbox id="smoking" v-model="form.contract.info.smoking" name="smoking">
                                {{ $t('Smoking in the car') }}
                            </app-custom-checkbox>
                        </div>
                        <div class="col-md-3 col-sm-12">
                            <app-input id="smoking_fine" v-model="form.smoking_fine" name="smoking_fine" rules="required">
                                <template #label>
                                    {{ $t('Suitsetamise trahv') }}*
                                </template>
                            </app-input>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col">
                            <app-input id="liability_sum" v-model="form.contract.info.liability_sum" name="liability_sum" rules="required">
                                <template #label>
                                    {{ $t('Liability') }}*
                                </template>
                            </app-input>
                        </div>
                        <div class="col">
                            <app-input id="tank_level" v-model="form.contract.info.tank_level" name="tank_level" rules="required">
                                <template #label>
                                    {{ $t('Tank level') }}*
                                </template>
                            </app-input>
                        </div>
                    </div>
                    <app-multi-lang-textarea id="damages"
                                             v-model="form.contract.info.vehicle_damages"
                                             name="damages"
                                             :langs="locales && Object.keys(locales)">
                        <template #label>
                            {{ $t('Damages') }}
                        </template>
                    </app-multi-lang-textarea>
                    <h4 class="mb-0">{{ $t('Period') }}</h4>
                    <app-datepicker id="search_dates"
                                    v-model="search_dates"
                                    range
                                    name="search_dates"
                                    @change="getPrice(booking.vehicle_id)"
                                    rules="required">
                        <template #label>
                            {{ $t('Start date') }} & {{ $t('Return date') }}*
                        </template>
                    </app-datepicker>
                    <div class="row">
                        <div class="col">
                            <app-datepicker id="start_time"
                                            v-model="start_time"
                                            inputType="time"
                                            name="start_time"
                                            @change="getPrice(booking.vehicle_id)"
                                            rules="required">
                                <template #label>
                                    {{ $t('Start time') }}*
                                </template>
                            </app-datepicker>
                        </div>
                        <div class="col">
                            <app-datepicker id="end_time"
                                            v-model="end_time"
                                            inputType="time"
                                            name="end_time"
                                            @change="getPrice(booking.vehicle_id)"
                                            rules="required">
                                <template #label>
                                    {{ $t('Return time') }}*
                                </template>
                            </app-datepicker>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col">
                            <app-select
                                id="pickup_location_id"
                                v-model="booking.pickup_location_id"
                                :options="locations.map(c => c.id)"
                                rules="required"
                                name="pickup_location_id"
                                openDirection="bottom"
                                @input="checkPaidServices"
                                searchable
                                not-empty
                                closeOnSelect
                                :custom-label="
                                    id =>
                                        locations.some(c => c.id === id)
                                            ? locations.find(c => c.id === id).name[locale]
                                            : null
                                ">
                                <template #label>
                                    {{ $t('Pick up from') }}*
                                </template>
                            </app-select>
                            <app-input v-if="booking.pickup_location_id === otherLocationId"
                                       id="pickup_location"
                                       v-model="booking.pickup_location_custom"
                                       :placeholder="`${$t('Address')}*`"
                                       @blur="checkPaidServices"
                                       name="pickup_location"
                                       rules="required"/>
                        </div>
                        <div class="col">
                            <app-select
                                id="return_location_id"
                                v-model="booking.return_location_id"
                                :options="locations.map(c => c.id)"
                                rules="required"
                                name="return_location_id"
                                openDirection="bottom"
                                @input="checkPaidServices"
                                searchable
                                not-empty
                                closeOnSelect
                                :custom-label="id => locations.some(c => c.id === id) ? locations.find(c => c.id === id).name[locale] : null">
                                <template #label>
                                    {{ $t('Return to') }}*
                                </template>
                            </app-select>
                            <app-input v-if="booking.return_location_id === otherLocationId"
                                       id="return_location"
                                       v-model="booking.return_location_custom"
                                       :placeholder="`${$t('Address')}*`"
                                       @blur="checkPaidServices"
                                       name="return_location"
                                       rules="required"/>
                        </div>
                    </div>
                    <app-input id="permitted_countries" v-model="form.contract.info.permitted_countries" name="permitted_countries" rules="required">
                        <template #label>
                            {{ $t('Permitted countries') }}
                        </template>
                    </app-input>
                    <div class="mt-2 d-flex justify-content-start">
                        <app-custom-checkbox id="travel_out_check" v-model="travel_out_check" name="travel_out_check">
                            {{ $t('Travel out of Estonia') }}
                        </app-custom-checkbox>
                    </div>
                    <app-input v-if="travel_out_check" id="travel_out" v-model="booking.travel_out" name="travel_out" rules="required">
                        <template #label> {{ $t('Countries') }}*</template>
                    </app-input>
                    <div class="row mt-2">
                        <div class="col-md-6 col-sm-12">
                            <div class="accordion" id="ad_eqs">
                                <div class="card shadow-none">
                                    <div class="card-header pointer accordeon-border" data-toggle="collapse" href="#additional_equipment">
                                        <a class="card-link font-weight-normal pl-1">
                                            {{ $t('Additional eq') }}
                                            <fa-icon :icon="['fas', 'angle-down']" pull="right"/>
                                        </a>
                                    </div>
                                    <div id="additional_equipment" class="collapse show pt-3" aria-labelledby="headingL-varustus" data-parent="#ad_eqs">
                                        <app-loader-gif v-if="loading"/>
                                        <template v-else>
                                            <app-custom-checkbox v-for="(eq, j) in additions"
                                                                 :id="`eq_${j}`"
                                                                 :name="`eq_${j}`"
                                                                 :key="j"
                                                                 :value="booking.additional_eq.map(e => e.id).includes(eq.id)"
                                                                 @input="addAddEq(eq)">
                                                {{ $t(eq.name[locale]) }}
                                                <small v-if="eq.all_prices && eq.all_prices.price">
                                                    ({{ ((eq.all_prices.price + eq.all_prices.added_price) / 100).toFixed(2) }} EUR)
                                                </small>
                                            </app-custom-checkbox>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-6 col-sm-12">
                            <div class="accordion" id="services_acc">
                                <div class="card shadow-none">
                                    <div class="card-header pointer accordeon-border" data-toggle="collapse" href="#services">
                                        <a class="card-link font-weight-normal pl-1">
                                            {{ $t('Services') }}
                                            <fa-icon :icon="['fas', 'angle-down']" pull="right"/>
                                        </a>
                                    </div>
                                    <div id="services" class="collapse show" aria-labelledby="headingL-varustus" data-parent="#services_acc">
                                        <div class="card-body">
                                            <app-loader-gif v-if="loading"/>
                                            <template v-else>
                                                <template v-if="booking.services && booking.services.length">
                                                    <template v-for="(serv, s) in booking.services">
                                                        <div class="d-flex justify-content-between" :key="`s_${s}`">
                                                            <p class="mb-1">
                                                                {{ serv.name[locale] }}, <strong>{{ (serv.price / 100).toFixed(2) }}€</strong>
                                                            </p>
                                                            <app-button native-type="button" type="danger" @click="removeService(s)" xsmall>
                                                                {{ $t('Delete') }}
                                                            </app-button>
                                                        </div>
                                                    </template>
                                                </template>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </validation-observer>
</template>

<script>
import ContractService from '@services/contract.service';
import {mapGetters} from 'vuex';
import VehicleService from '@services/vehicle.service';
import LocationService from '@services/location.service';
import ClientService from '@services/client.service';
import PublicSettingsService from '@services/publicSettings.service';
import PublicBookingService from '@services/publicBooking.service';

export default {
    data() {
        return {
            clients: [],
            search_dates: [],
            existing_client: false,
            travel_out_check: false,
            manual_vehicle: false,
            chosen_client: null,
            start_time: '',
            end_time: '',
            form: {
                contract: {
                    rentee: {
                        name: null,
                        idcode: null,
                        phone: null,
                        drivers_license: null,
                        passport: null,
                        address: null,
                        email: null,
                        cc_no: null,
                        cc_validity: null,
                    },
                    company: {
                        name: null,
                        reg_no: null,
                        address: null,
                        email: null,
                    },
                    add_drivers: [
                        {name: null, drivers_license: null, passport: null},
                        {name: null, drivers_license: null, passport: null}
                    ],
                    info: {
                        smoking: false,
                        eq_and_serv: null,
                        extra_info: null,
                        liability_sum: null,
                        tank_level: null,
                        vehicle_clean: true,
                        vehicle_damages: {
                            et: null,
                            en: null
                        },
                        permitted_countries: null,
                        additional_drivers: true,
                        notes: null,
                    },
                    client_id: false
                },
                add_client: true,
                smoking_fine: 200
            },
            loading: false,
            promocode: null,
            original_bond: 0,
            discount: 0,
            booking: {
                client_id: null,
                vehicle_id: null,
                prices: {},
                vehicle: {
                    make: {
                        name: null
                    },
                    model: {
                        name: null
                    },
                    fuel: {
                        name: null
                    },
                    reg_no: null,
                    vin: null,
                    damages: {
                        et: null,
                        en: null
                    }
                },
                added_price: 0,
                edit_price: true,
                tos: true,
                newsletter: false,
                start_date: null,
                travel_out: null,
                end_date: null,
                start_time: null,
                end_time: null,
                promo: null,
                send_email: false,
                pickup_location_custom: '',
                return_location_custom: '',
                services: [],
                additional_eq: [],
            },
            client: {},
            additions: [],
            locations: [],
            vehicles: [],
        };
    },
    computed: {
        ...mapGetters(['locale', 'locales', 'langs', 'localeDateFormat', 'otherLocationId'])
    },
    methods: {
        manualVehicleClicked() {
            if (this.manual_vehicle) {
                this.booking.vehicle_id = null;
            }
        },
        existingClientClicked() {
            if (!this.existing_client) {
                this.form.contract.rentee = {
                    name: null,
                    idcode: null,
                    phone: null,
                    drivers_license: null,
                    passport: null,
                    address: null,
                    email: null,
                    cc_no: null,
                    cc_validity: null,
                };
                this.form.contract.company = {
                    name: null,
                    reg_no: null,
                    address: null,
                    email: null,
                };
                this.form.client_id = null;
                this.booking.client = {};
                this.booking.prices.bond = this.original_bond;
            }
        },
        removeService(index) {
            this.booking.services.splice(index, 1);
            this.fillEqAndServices();
        },
        chooseClient(client) {
            this.form.contract.rentee = {
                name: client.name,
                idcode: client.idcode,
                phone: client.phone,
                drivers_license: client.drivers_license,
                passport: client.id_card,
                address: client.address,
                email: client.email,
                cc_no: client.cc_no,
                cc_validity: client.cc_validity,
            };
            this.form.contract.company = {
                name: client.company_name,
                reg_no: client.company_reg,
                email: client.company_email,
                address: client.address,
            };
            this.form.client_id = client.id;
            this.booking.client = client;
            this.booking.client_id = client.id;
            if (!client.bond) {
                this.booking.prices.bond = 0;
            } else {
                this.booking.prices.bond = this.original_bond;
            }
            this.checkClientDiscount();
        },
        getClients() {
            this.loading = true;
            ClientService.clients().then(res => {
                this.loading = false;
                this.clients = res;
            });
        },
        checkClientDiscount() {
            const queryObj = {
                phone: this.form.contract.rentee.phone,
                email: this.form.contract.rentee.email
            };
            PublicBookingService.checkClientDiscount(queryObj).then(res => {
                if (res) {
                    this.discount = res.discount;
                    this.form.client_id = res.id;
                    this.booking.client_id = res.id;
                    this.booking.promocode_id = null;
                } else {
                    this.discount = 0;
                    this.booking.client_id = null;
                    this.form.client_id = null;
                    this.checkPromo();
                }
            });
        },
        checkPromo() {
            if (!this.search_dates.length) {
                return false;
            }
            const queryObj = {
                code: this.promocode,
                start_date: this.search_dates[0],
                end_date: this.search_dates[1]
            };
            PublicBookingService.checkPromocode(queryObj).then(res => {
                if (res) {
                    this.discount = res.discount;
                    this.booking.promocode_id = res.id;
                    this.booking.client_id = null;
                    this.form.client_id = null;
                } else {
                    this.discount = 0;
                    this.booking.promocode_id = null;
                }
            });
        },
        store(validate) {
            validate().then(valid => {
                if (valid) {
                    this.loading = true;
                    this.form.booking = this.booking;
                    this.form.booking.start_date = this.search_dates[0];
                    this.form.booking.end_date = this.search_dates[1];
                    this.form.booking.start_time = this.start_time;
                    this.form.booking.end_time = this.end_time;
                    this.form.booking.pickup_custom = this.form.booking.pickup_location_id === this.otherLocationId ? 1 : 0,
                    this.form.booking.return_custom = this.form.booking.return_location_id === this.otherLocationId ? 1 : 0,
                    this.form.contract.liability_sum = this.form.contract.liability_sum * 100;

                    if (this.form.booking.edit_price) {
                        this.setManualPrices();
                    }

                    ContractService.storeEmpty(this.form).then(res => {
                        this.$toastr.s(this.$t('Saved'));
                        this.loading = false;
                        this.$router.push({name: 'contracts.show', params: {contract_id: res.id}});
                    }).catch(err => {
                        console.log(err);
                        this.loading = false;
                    });
                }
            });
        },
        setManualPrices() {
            this.booking.prices.eq_total = this.booking.prices.eq_total * 100;
            this.booking.prices.service_total = this.booking.prices.service_total * 100;
            this.booking.prices.avg_price = this.booking.prices.avg_price * 100;
            this.booking.prices.bond = this.booking.prices.bond * 100;
            this.booking.prices.total = this.booking.prices.total * 100;
            this.booking.prices.added_price = this.booking.prices.added_price * 100;
            this.booking.prices.hr_price = this.booking.prices.hr_price * 100;
            this.booking.prices.over_hrs = this.booking.prices.over_hrs ?? 0;
        },
        getVehicleName(id) {
            const vehicle = this.vehicles.some(c => c.id === id) ? this.vehicles.find(c => c.id === id) : null;
            if (vehicle) {
                return `${vehicle.make.name} ${vehicle.model.name} ${vehicle.slug.slice(-1)}, ${vehicle.reg_no}`;
            }
            return vehicle;
        },
        getPrice(vehicleId, checkService = true) {
            const vehicle = this.vehicles.find(veh => veh.id === vehicleId);
            if (!vehicle) {
                return false;
            }
            this.form.contract.info.vehicle_damages = vehicle.damages;

            if (!this.booking.vehicle_id) {
                return false;
            }
            if (!this.search_dates.length) {
                return false;
            }
            const queryObj = {
                vehicle_id: vehicle.id,
                slug: vehicle.slug,
                discount: this.discount,
                start_date: this.search_dates[0],
                end_date: this.search_dates[1],
                additional_eq: this.booking.additional_eq,
                pickup_location_id: this.booking.pickup_location_id,
                pickup_custom: this.booking.pickup_location_id === this.otherLocationId ? 1 : 0,
                return_location_id: this.booking.return_location_id,
                return_custom: this.booking.return_location_id === this.otherLocationId ? 1 : 0,
            };
            if (this.start_time) {
                queryObj.start_time = this.start_time;
            }
            if (this.end_time) {
                queryObj.end_time = this.end_time;
            }

            this.getVehicle(queryObj);
            this.getAddEqPrices(queryObj);
            if (checkService) {
                this.checkPaidServices();
            }
        },
        getVehicle(queryObj) {
            PublicBookingService.getVehicle(queryObj).then(res => {
                this.vehicle = res.vehicle;
                if (!this.booking.edit_price) {
                    this.booking.services = res.vehicle.services;
                }
                if (!this.booking.edit_price) {
                    this.setPrices(res.vehicle.all_prices);
                }
                this.checkIsVehicleBooked();
            });
        },
        getAddEqPrices(queryObj) {
            PublicSettingsService.addEq(queryObj).then(res => {
                this.additions = res;
                this.fillEqAndServices();
            });
        },
        checkPaidServices() {
            this.checkIsVehicleBooked();
            if (!this.start_time || !this.end_time || !this.search_dates || !this.booking.pickup_location_id || !this.booking.return_location_id || !this.booking.vehicle_id) {
                return false;
            }
            if (this.booking.edit_price) {
                return false;
            }
            const queryObj = {
                start_date: this.search_dates[0],
                end_date: this.search_dates[1],
                start_time: this.start_time,
                end_time: this.end_time,
                pickup_location_id: this.booking.pickup_location_id,
                pickup_custom: this.booking.pickup_location_id === this.otherLocationId ? 1 : 0,
                return_location_id: this.booking.return_location_id,
                return_custom: this.booking.return_location_id === this.otherLocationId ? 1 : 0,
            };

            PublicBookingService.checkPaidServices(this.booking.vehicle_id, queryObj).then(res => {
                this.booking.services = res;
                this.getPrice(this.booking.vehicle_id, false);
            });
        },
        checkIsVehicleBooked() {
            if (!this.start_time || !this.end_time || !this.search_dates || !this.booking.vehicle_id) {
                return false;
            }
            const queryObj = {
                start_date: this.search_dates[0],
                end_date: this.search_dates[1],
                start_time: this.start_time,
                end_time: this.end_time,
                vehicle_id: this.booking.vehicle_id,
            };

            PublicBookingService.checkIsVehicleBooked(queryObj);
        },
        addAddEq(eq) {
            const ids = this.booking.additional_eq.map(e => e.id);
            const index = ids.indexOf(eq.id);

            if (index > -1) {
                this.booking.additional_eq.splice(index, 1);
                this.fillEqAndServices();
            } else {
                this.booking.additional_eq.push(eq);
                this.fillEqAndServices();
            }
        },
        setPrices(prices) {
            this.original_bond = prices.bond / 100;
            this.booking.prices.eq_total = (prices.eq_total / 100).toFixed(2);
            this.booking.prices.service_total = prices.service_total / 100;
            this.booking.prices.avg_price = (prices.avg_price / 100).toFixed(2);
            this.booking.prices.days = prices.days;
            this.booking.prices.bond = prices.bond / 100;
            this.booking.prices.total = (prices.total / 100).toFixed(2);
            this.booking.prices.added_price = (prices.added_price / 100).toFixed(2);
            this.booking.prices.hr_price = (prices.hr_price / 100).toFixed(2);
            this.booking.prices.over_hrs = prices.over_hrs;
        },
        // getBooking(bookingId) {
        //     BookingService.booking(bookingId).then(res => {
        //         this.booking = res;
        //         if (this.booking.travel_out) {
        //             this.travel_out_check = true;
        //         }
        //         this.setPrices(res.prices);
        //         this.client = res.client;
        //         if (res.client) {
        //             this.form.contract.rentee = {
        //                 name: res.client.name,
        //                 idcode: res.client.idcode,
        //                 drivers_license: res.client.drivers_license,
        //                 passport: res.client.id_card,
        //                 address: res.client.address,
        //                 email: res.client.email,
        //                 phone: res.client.phone,
        //                 cc_no: res.client.cc_no,
        //                 cc_validity: res.client.cc_validity
        //             };
        //             this.form.contract.company = {
        //                 name: res.client.company_name,
        //                 reg_no: res.client.company_reg,
        //                 address: res.client.address,
        //                 email: res.client.company_email,
        //             };
        //         } else {
        //             this.form.contract.rentee = {
        //                 name: res.fname + ' ' + res.lname,
        //                 address: res.address,
        //                 phone: res.phone,
        //                 email: res.email
        //             };
        //             this.form.contract.company = {
        //                 name: null
        //             };
        //         }
        //         this.search_dates = [moment(res.start_time).format(this.localeDateFormat), moment(res.end_time).format(this.localeDateFormat)];
        //         this.start_time = moment(res.start_time).format('HH:mm');
        //         this.end_time = moment(res.end_time).format('HH:mm');
        //         this.form.contract.info.liability_sum = res.vehicle.liability_sum / 100;
        //         this.getAdditions();
        //     });
        // },
        // getAdditions() {
        //     const queryObj = {
        //         vehicle_id: this.booking.vehicle_id,
        //         slug: this.booking.vehicle.slug,
        //         discount: this.discount,
        //         start_date: this.search_dates[0],
        //         end_date: this.search_dates[1],
        //         additional_eq: this.booking.additional_eq,
        //         pickup_location_id: this.booking.pickup_location_id,
        //         pickup_custom: this.booking.pickup_location_id === this.otherLocationId ? 1 : 0,
        //         return_location_id: this.booking.return_location_id,
        //         return_custom: this.booking.return_location_id === this.otherLocationId ? 1 : 0,
        //     };
        //     if(this.start_time) {
        //         queryObj.start_time = this.start_time;
        //     }
        //     if(this.end_time) {
        //         queryObj.end_time = this.end_time;
        //     }
        //
        //     PublicSettingsService.addEq(queryObj).then(res => {
        //         this.additions = res;
        //         this.fillEqAndServices();
        //     });
        // },
        fillEqAndServices() {
            const names = [];
            this.booking.additional_eq.map(e => {
                this.additions.map(eq => {
                    if (eq.id === e.id) {
                        names.push(`${eq.name[this.locale]} ${eq.all_prices.price / 100}EUR`);
                    }
                });
            });
            this.booking.services.map(serv => {
                names.push(`${serv.name[this.locale]} ${serv.price / 100}EUR`);
            });
            this.form.contract.info.eq_and_serv = names.join(', ');
        },
        getVehicles() {
            VehicleService.noEmpties().then(res => this.vehicles = res);
        },
        getLocations() {
            LocationService.locations().then(res => this.locations = res);
        },
    },
    created() {
        // this.getBooking(this.$route.params.booking_id);
        this.getLocations();
        this.getVehicles();
        this.getClients();
        this.form.contract.info.permitted_countries = this.$t('Estonia');
        this.form.contract.info.tank_level = this.$t('Full tank');
    }
};
</script>
